// Full updated Demographic.js with upload field for each document and ear dropdowns in Provisional Diagnosis

import React, { useState } from 'react';
import {
  Paper, Typography, Card, CardContent, Grid, TextField,
  Select, MenuItem, FormControl, InputLabel, Divider, Button,
  Checkbox, FormControlLabel, FormGroup, Box,  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';


const Demographic = ({ onSubmit }) => {
  const [formData, setFormData] = useState({
    serialNo: '', name: '', caseNumber: '', dob: '', age: '', gender: '',
    parents: {
      father: { name: '', education: '', occupation: '' },
      mother: { name: '', education: '', occupation: '' },
      guardian: { name: '', education: '' },
    },
    education: '', occupation: '', address: '', phoneNo: '', familyIncome: '',
    onsetType: 'congenital', acquiredAge: '',
    comorbidities: {
      ID: false, Autism: false, Syndrome: false,
      HeartDisease: false, ADHD: false, others: false, othersText: ''
    },
    hearingAid: {
      right: { type: '', duration: { days: '', months: '', years: '' }, usage: '', hours: '' },
      left: { type: '', duration: { days: '', months: '', years: '' }, usage: '', hours: '' },
    },
    provisionalDiagnosis: '',
    provisionalDiagnosisEar: { right: '', left: '' },
    documents: {
      audiological: null, radiological: null, psychological: null, therapyProgress: null,
      aidedAudiogram: null, aadharCard: null, incomeCasteCert: null, birthCert: null,
      disabilityCert: null, entClearance: null, speechEvaluation: null
    }
  });
   const [loading, setLoading] = useState(false);
    const [saveStatus, setSaveStatus] = useState({ message: '', severity: '' });
    const [openPasswordModal, setOpenPasswordModal] = useState(false);
    const [password, setPassword] = useState('');
    const [downloadFormat, setDownloadFormat] = useState('excel');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  const handleDemographicChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleNestedChange = (mainField, subField, value) => {
    setFormData((prev) => ({
      ...prev,
      [mainField]: {
        ...prev[mainField],
        [subField]: value,
      },
    }));
  };
  const handleComorbidityChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      comorbidities: {
        ...prev.comorbidities,
        [name]: checked
      }
    }));
  };
    const handleComorbidityTextChange = (e) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      comorbidities: {
        ...prev.comorbidities,
        othersText: value
      }
    }));
  };

    const handleHearingAidChange = (ear, field, value) => {
    setFormData(prev => ({
      ...prev,
      hearingAid: {
        ...prev.hearingAid,
        [ear]: {
          ...prev.hearingAid[ear],
          [field]: value
        }
      }
    }));
  };


  const handleDownloadClick = () => {
    setOpenPasswordModal(true);
  };

const handleDownloadConfirm = async () => {
  const correctKey = "Varun@6361";

  // Validate key entered in modal
  if (password !== correctKey) {
    alert("Incorrect key! Download canceled.");
    setPassword('');
    setOpenPasswordModal(false);
    return;
  }

  setOpenPasswordModal(false);
  setLoading(true);
  setSaveStatus({
    message: `Requesting Demographic Data ${downloadFormat}...`,
    severity: "info",
  });

  try {
    const resp = await fetch(
      `${process.env.REACT_APP_API_URL}/api/reports/download-form`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          table: "demographic_data",
          formName: "Demographic_Data",
          format: downloadFormat,
          key: password   // 🔥 send KEY instead of password
        }),
      }
    );

    if (!resp.ok) {
      const body = await resp.json().catch(() => ({}));
      setSaveStatus({
        message: `Failed: ${body.message || resp.statusText}`,
        severity: "error",
      });
      return;
    }

    const blob = await resp.blob();
    const disposition = resp.headers.get("content-disposition") || "";

    let filename = `Demographic_Data_${Date.now()}.${
      downloadFormat === "excel" ? "xlsx" : "pdf"
    }`;

    const match = /filename="?([^"]+)"?/.exec(disposition);
    if (match && match[1]) filename = match[1];

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);

    setSaveStatus({
      message: `Download started: ${filename}`,
      severity: "success",
    });

  } catch (err) {
    console.error("Download error:", err);
    setSaveStatus({
      message: `Error downloading file: ${err.message}`,
      severity: "error",
    });
  } finally {
    setLoading(false);
    setPassword('');
  }
};


  const handlePasswordModalClose = () => {
    setOpenPasswordModal(false);
    setPassword('');
  };

    const handleHearingAidDurationChange = (ear, field, value) => {
    setFormData(prev => ({
      ...prev,
      hearingAid: {
        ...prev.hearingAid,
        [ear]: {
          ...prev.hearingAid[ear],
          duration: {
            ...prev.hearingAid[ear].duration,
            [field]: value
          }
        }
      }
    }));
  };
  const handleEarDropdown = (ear, val) => {
    setFormData(prev => ({
      ...prev,
      provisionalDiagnosisEar: {
        ...prev.provisionalDiagnosisEar,
        [ear]: val
      }
    }));
  };

  const handleFileUpload = (key, file) => {
    setFormData(prev => ({
      ...prev,
      documents: {
        ...prev.documents,
        [key]: file
      }
    }));
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  
  try {
    const submitData = new FormData();
    submitData.append('formData', JSON.stringify(formData));

    // Append all files to FormData
    Object.entries(formData.documents).forEach(([key, file]) => {
      if (file) {
        submitData.append(key, file);
      }
    });

    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/demographic`, {
      method: 'POST',
      body: submitData,
    });

    if (response.ok) {
      const result = await response.json();
      alert('Demographic data saved successfully!');
      // Reset form or navigate as needed
    } else {
      const errorData = await response.json();
      alert(`Failed to save: ${errorData.error}`);
    }
  } catch (error) {
    console.error('Error submitting form:', error);
    alert('Error submitting demographic data');
  }
};

  return (
    <Paper elevation={3} sx={{ p: 4, maxWidth: 1400, m: 'auto', mt: 4, mb: 6 }}>
          {/* Download Button - Top Right */}
            <Box sx={{ position: 'absolute', top: 350, right: 140 }}>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadClick}
                disabled={loading}
                sx={{ 
                  borderRadius: 2,
                  boxShadow: 2,
                  '&:hover': {
                    boxShadow: 4,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.3s ease'
                  }
                }}
              >
                Download 
              </Button>
            </Box>
      <Typography variant="h4" gutterBottom fontWeight="bold" color="secondary" align="center">
        Demographic Details Form
      </Typography>

      {/* Personal Information Section */}
      <Typography variant="h5" gutterBottom fontWeight="bold" color="secondary" align="left" sx={{ mt: 2 }}>
        Personal Information
      </Typography>
      <Card elevation={2} sx={{ borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Serial No" name="serialNo" value={formData.serialNo} onChange={handleDemographicChange} size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Name" name="name" value={formData.name} onChange={handleDemographicChange} size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Case Number" name="caseNumber" value={formData.caseNumber} onChange={handleDemographicChange} size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="DOB" name="dob" type="date" value={formData.dob} onChange={handleDemographicChange} InputLabelProps={{ shrink: true }} size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Age" name="age" value={formData.age} onChange={handleDemographicChange} size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel shrink={true}>Gender</InputLabel>
                <Select name="gender" value={formData.gender} onChange={handleDemographicChange} label="Gender"
                displayEmpty
                 renderValue={value => value || "Select Gender"}
                >
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Parent Details Section */}
      <Typography variant="h5" gutterBottom fontWeight="bold" color="secondary" align="left">
        Parent Details
      </Typography>
      <Card elevation={2} sx={{ borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'medium', color: '#555' }}>
                Father
              </Typography> 
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Name" value={formData.parents.father.name} onChange={(e) => handleNestedChange('parents', 'father', { ...formData.parents.father, name: e.target.value })} size="small" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Education" value={formData.parents.father.education} onChange={(e) => handleNestedChange('parents', 'father', { ...formData.parents.father, education: e.target.value })} size="small" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Occupation" value={formData.parents.father.occupation} onChange={(e) => handleNestedChange('parents', 'father', { ...formData.parents.father, occupation: e.target.value })} size="small" />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'medium', color: '#555' }}>
                Mother
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Name" value={formData.parents.mother.name} onChange={(e) => handleNestedChange('parents', 'mother', { ...formData.parents.mother, name: e.target.value })} size="small" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Education" value={formData.parents.mother.education} onChange={(e) => handleNestedChange('parents', 'mother', { ...formData.parents.mother, education: e.target.value })} size="small" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Occupation" value={formData.parents.mother.occupation} onChange={(e) => handleNestedChange('parents', 'mother', { ...formData.parents.mother, occupation: e.target.value })} size="small" />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'medium', color: '#555' }}>
                Guardian
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Name" value={formData.parents.guardian.name} onChange={(e) => handleNestedChange('parents', 'guardian', { ...formData.parents.guardian, name: e.target.value })} size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Education" value={formData.parents.guardian.education} onChange={(e) => handleNestedChange('parents', 'guardian', { ...formData.parents.guardian, education: e.target.value })} size="small" />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Additional Information Section */}
      <Typography variant="h5" gutterBottom fontWeight="bold" color="secondary" align="left">
        Additional Information
      </Typography>
      <Card elevation={2} sx={{ borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Education" name="education" value={formData.education} onChange={handleDemographicChange} size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Occupation" name="occupation" value={formData.occupation} onChange={handleDemographicChange} size="small" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Address" name="address" value={formData.address} onChange={handleDemographicChange} multiline rows={3} size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Phone No" name="phoneNo" value={formData.phoneNo} onChange={handleDemographicChange} size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel shrink={true}>Family Income</InputLabel>
                <Select name="familyIncome" value={formData.familyIncome} onChange={handleDemographicChange} label="Family Income"
                displayEmpty
                renderValue={value => value || "Select Family Income"}
                >
                  <MenuItem value="Low">Low</MenuItem>
                  <MenuItem value="Mid">Mid</MenuItem>
                  <MenuItem value="High">High</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Onset of Hearing Loss */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                Age of Onset of Hearing Loss
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel shrink={true}>Onset Type</InputLabel>
                <Select 
                  name="onsetType" 
                  value={formData.onsetType} 
                  onChange={handleDemographicChange} 
                  label="Onset Type"
                  displayEmpty
                  
                  renderValue={value => value || "Select Onset Type"}q
                >
                  <MenuItem value="congenital">Congenital</MenuItem>
                  <MenuItem value="acquired">Acquired</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {formData.onsetType === 'acquired' && (
              <Grid item xs={12} sm={6}>
                <TextField 
                  fullWidth 
                  label="Age at Acquisition" 
                  name="acquiredAge" 
                  value={formData.acquiredAge} 
                  onChange={handleDemographicChange} 
                  size="small" 
                />
              </Grid>
            )}
            
            {/* Comorbidities */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, mt: 2 }}>
                Comorbidities
              </Typography>
              <FormGroup row>
                {['ID', 'Autism', 'Syndrome', 'HeartDisease', 'ADHD'].map((condition) => (
                  <FormControlLabel
                    key={condition}
                    control={
                      <Checkbox 
                        checked={formData.comorbidities[condition]} 
                        onChange={handleComorbidityChange} 
                        name={condition} 
                      />
                    }
                    label={condition === 'HeartDisease' ? 'Heart Disease' : condition}
                  />
                ))}
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={formData.comorbidities.others} 
                      onChange={handleComorbidityChange} 
                      name="others" 
                    />
                  }
                  label="Others"
                />
                {formData.comorbidities.others && (
                  <TextField 
                    size="small"
                    value={formData.comorbidities.othersText}
                    onChange={handleComorbidityTextChange}
                    placeholder="Specify other conditions"
                    sx={{ ml: 2, width: 200 }}
                  />
                )}
              </FormGroup>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Hearing Aid Details Section */}
      <Typography variant="h5" gutterBottom fontWeight="bold" color="secondary" align="left">
        Hearing Aid Details
      </Typography>
      <Card elevation={2} sx={{ borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Grid container spacing={3}>
            {/* Right Ear */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, textAlign: 'center' }}>
                Right Ear
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField 
                    fullWidth 
                    label="Type of Hearing Aid" 
                    value={formData.hearingAid.right.type} 
                    onChange={(e) => handleHearingAidChange('right', 'type', e.target.value)} 
                    size="small" 
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                    Duration of Hearing Aid Use
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <TextField 
                    fullWidth 
                    label="Days" 
                    value={formData.hearingAid.right.duration.days} 
                    onChange={(e) => handleHearingAidDurationChange('right', 'days', e.target.value)} 
                    size="small" 
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField 
                    fullWidth 
                    label="Months" 
                    value={formData.hearingAid.right.duration.months} 
                    onChange={(e) => handleHearingAidDurationChange('right', 'months', e.target.value)} 
                    size="small" 
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField 
                    fullWidth 
                    label="Years" 
                    value={formData.hearingAid.right.duration.years} 
                    onChange={(e) => handleHearingAidDurationChange('right', 'years', e.target.value)} 
                    size="small" 
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel shrink={true}>Usage</InputLabel>
                    <Select 
                      value={formData.hearingAid.right.usage} 
                      onChange={(e) => handleHearingAidChange('right', 'usage', e.target.value)} 
                      label="Usage"
                      displayEmpty
                      renderValue={value => value || "Select Usage"}
                    >
                      <MenuItem value="Full time">Full time</MenuItem>
                      <MenuItem value="Part time">Part time</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField 
                    fullWidth 
                    label="Hours of Usage" 
                    value={formData.hearingAid.right.hours} 
                    onChange={(e) => handleHearingAidChange('right', 'hours', e.target.value)} 
                    size="small" 
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Left Ear */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, textAlign: 'center' }}>
                Left Ear
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField 
                    fullWidth 
                    label="Type of Hearing Aid" 
                    value={formData.hearingAid.left.type} 
                    onChange={(e) => handleHearingAidChange('left', 'type', e.target.value)} 
                    size="small" 
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                    Duration of Hearing Aid Use
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <TextField 
                    fullWidth 
                    label="Days" 
                    value={formData.hearingAid.left.duration.days} 
                    onChange={(e) => handleHearingAidDurationChange('left', 'days', e.target.value)} 
                    size="small" 
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField 
                    fullWidth 
                    label="Months" 
                    value={formData.hearingAid.left.duration.months} 
                    onChange={(e) => handleHearingAidDurationChange('left', 'months', e.target.value)} 
                    size="small" 
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField 
                    fullWidth 
                    label="Years" 
                    value={formData.hearingAid.left.duration.years} 
                    onChange={(e) => handleHearingAidDurationChange('left', 'years', e.target.value)} 
                    size="small" 
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel shrink={true}>Usage</InputLabel>
                    <Select 
                      value={formData.hearingAid.left.usage} 
                      onChange={(e) => handleHearingAidChange('left', 'usage', e.target.value)} 
                      label="Usage"
                      displayEmpty
                      renderValue={value => value || "Select Usage"}
                    >
                      <MenuItem value="Full time">Full time</MenuItem>
                      <MenuItem value="Part time">Part time</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField 
                    fullWidth 
                    label="Hours of Usage" 
                    value={formData.hearingAid.left.hours} 
                    onChange={(e) => handleHearingAidChange('left', 'hours', e.target.value)} 
                    size="small" 
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Provisional Diagnosis Section */}
      <Typography variant="h5" gutterBottom fontWeight="bold" color="secondary" align="left">
        Medical Information
      </Typography>
      <Card elevation={2} sx={{ borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Provisional Diagnosis"
                name="provisionalDiagnosis"
                value={formData.provisionalDiagnosis}
                onChange={handleChange}
                multiline
                rows={3}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel shrink={true}>Right Ear Diagnosis</InputLabel>
                <Select
                  value={formData.provisionalDiagnosisEar.right}
                  onChange={(e) => handleEarDropdown('right', e.target.value)}
                  label="Right Ear Diagnosis"
                  displayEmpty
                  renderValue={value => value || "Select Diagnosis"}
                >
                  <MenuItem value="Normal">Normal</MenuItem>
                  <MenuItem value="Conductive">Conductive</MenuItem>
                  <MenuItem value="Sensorineural">Sensorineural</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel shrink={true}>Left Ear Diagnosis</InputLabel>
                <Select
                  value={formData.provisionalDiagnosisEar.left}
                  onChange={(e) => handleEarDropdown('left', e.target.value)}
                  label="Left Ear Diagnosis"
                  displayEmpty
                  renderValue={value => value || "Select Diagnosis"}
                >
                  <MenuItem value="Normal">Normal</MenuItem>
                  <MenuItem value="Conductive">Conductive</MenuItem>
                  <MenuItem value="Sensorineural">Sensorineural</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Document Upload Section */}
      <Typography variant="h5" gutterBottom fontWeight="bold" color="secondary" align="left">
        Required Documents
      </Typography>
      <Card elevation={2} sx={{ borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            {[
              { label: 'Audiological Report', key: 'audiological' },
              { label: 'Radiological Report', key: 'radiological' },
              { label: 'Psychological Report', key: 'psychological' },
              { label: 'Therapy Progress Report', key: 'therapyProgress' },
              { label: 'Aided Audiogram', key: 'aidedAudiogram' },
              { label: 'Aadhar Card (Parents and child)', key: 'aadharCard' },
              { label: 'Income and Caste Certificate', key: 'incomeCasteCert' },
              { label: 'Birth Certificate', key: 'birthCert' },
              { label: 'Disability Certificate', key: 'disabilityCert' },
              { label: 'ENT Clearance', key: 'entClearance' },
              { label: 'Speech Evaluation Report', key: 'speechEvaluation' },
            ].map((doc, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {doc.label}
                </Typography>
                <Button
                  component="label"
                  variant="contained"
                  fullWidth
                  color={formData.documents[doc.key] ? 'success' : '#1976d2'}
                >
                  {formData.documents[doc.key] ? 'Uploaded ✓' : 'Upload'}
                  <input
                    type="file"
                    hidden
                    onChange={(e) => handleFileUpload(doc.key, e.target.files[0])}
                  />
                </Button>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
 {/* Password Modal */}
      <Dialog 
        open={openPasswordModal} 
        onClose={handlePasswordModalClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Download Data
          <IconButton
            aria-label="close"
            onClick={handlePasswordModalClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, mb: 2 }}>
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              variant="outlined"
              sx={{ mb: 3 }}
            />
            <FormControl fullWidth>
              <InputLabel>Format</InputLabel>
              <Select
                value={downloadFormat}
                onChange={(e) => setDownloadFormat(e.target.value)}
                label="Format"
              >
                <MenuItem value="excel">Excel</MenuItem>
                <MenuItem value="pdf">PDF</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handlePasswordModalClose} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleDownloadConfirm} 
            variant="contained" 
            color="primary"
            disabled={!password}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          sx={{ px: 6, py: 1.5, fontSize: '1.1rem' }}
        >
          Submit
        </Button>
      </Box>
    </Paper>
  );
};

export default Demographic;
